import {
  Group,
  OnlyShow,
  Paper,
  Skeleton,
  createStyles,
  useScrollIntoView,
  HtmlText,
  Link,
} from '@ingeniorforeningen/figurine-core';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { getFormattedDateWithWeekDayFromString } from '../../helpers/casesHelpers';
import InfoMessage from './InfoMessage';
import {
  AppLanguage,
  AppendDocument,
  CaseDetails,
  CaseMessage,
  CaseState,
  Errors,
  MessagesTexts,
} from '../../types';
import { replaceHostName, stripText } from '../../helpers/textsHelpers';
import { useEffect, useState } from 'react';
import { useIsColumn } from '../../hooks';
import './PrintStyling.css';

import CreateMessage from './Messages/CreateMessage';
import { EventItem, RenderMessages } from './Messages/RenderMessages';

const styles = createStyles((theme) => ({
  paper: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '0!important',
  },
  closed: {
    boxShadow: 'none',
    width: '100%',
    backgroundColor: theme.other.colorTokens.backgrounds.neutral,
  },
  messageWrapper: {
    [theme.fn.smallerThan('sm')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.other.colorTokens.backgrounds.neutral,
      zIndex: 399,
    },
  },
  printButton: {
    fontSize: '0.75rem',
    textAlign: 'right',

    '&:visited': {
      color: '#276095 !important',
    },
  },
}));

// can't make scroll work with `createStyles`
const ReverseScrollArea = styled.div`
    display: flex;
    height 100%;
    overflow-y: auto;
    overflow-x: hidden;
    flex-direction: column-reverse;
    flexWrap: nowrap;
    overscroll-behavior: contain;
  `;

type MessagesProps = {
  loading: boolean;
  language: AppLanguage;
  caseDetails: CaseDetails | null;
  caseMessages: CaseMessage[] | null;
  handlePostMessage: (message: Message) => void;
  messagesTexts?: MessagesTexts;
  errorTexts?: Errors;
  username?: string;
  postingCaseState: { isPosting: boolean; error: string | null; isPosted: boolean };
};

export type Message = { message: string; documents: string[] };

export const Messages = ({
  loading,
  language,
  caseDetails,
  caseMessages,
  handlePostMessage,
  messagesTexts,
  errorTexts,
  username,
  postingCaseState,
}: MessagesProps) => {
  const { classes } = styles();
  const { isColumn } = useIsColumn();

  const { scrollIntoView, targetRef: scrollTOtargetRef } = useScrollIntoView<HTMLDivElement>({
    duration: 3000,
    easing: (x) => 1 - Math.pow(1 - x, 4),
  });

  const [items, setItems] = useState<EventItem[]>([]);

  useEffect(() => {
    if (!caseMessages || !caseDetails) {
      return;
    }
    setItems([
      ...caseMessages?.map((x) => ({
        id: x.caseNoteId.toString(),
        type: 'message',
        timestamp: x.creationDate,
        message: x,
      })),
      caseDetails?.creationDate && {
        id: 'created',
        type: 'created',
        timestamp: caseDetails?.creationDate,
      },
      caseDetails?.state === CaseState.closed && {
        id: 'closed',
        type: 'closed',
        timestamp: caseDetails?.completionDate,
      },
    ] as EventItem[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseMessages, caseDetails]);

  useEffect(() => {
    if (!postingCaseState.isPosting) {
      setItems((prevItems: EventItem[]) =>
        prevItems.map((item: any) => {
          if (item.message) {
            return { ...item, message: { ...item.message, posting: false } };
          }
          return item;
        }),
      );
    } else {
      setItems((prevItems: EventItem[]) => prevItems.filter((item: any) => !item.message?.error));
    }
    if (postingCaseState.error) {
      setItems((prevItems: any) =>
        prevItems.map((item: any) => {
          if (item.message && item.id === 'tempMessage') {
            return {
              ...item,
              message: { ...item.message, posting: false, error: postingCaseState.error },
            };
          }
          return item;
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingCaseState]);

  useEffect(() => {
    if (!caseMessages || !isColumn) {
      return;
    }
    setTimeout(() => {
      scrollIntoView();
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseMessages, isColumn]);

  if (loading || !messagesTexts || !caseMessages || !caseDetails || !errorTexts) {
    return (
      <Paper className={classes.paper} cypress="skeleton">
        <Group justify="center" align="flex-end" p="xl" m={0} style={{ flex: 1 }}>
          <Group direction="column" p="xl" style={{ flex: 1 }}>
            <Group direction="column" align="flex-end" style={{ flex: 1 }}>
              <Skeleton width="75%" height="8rem" style={{ borderTopRightRadius: 0 }} />
              <Skeleton width="6rem" height="1rem" />
            </Group>
            <Group direction="column" style={{ flex: 1 }} mt="md">
              <Skeleton width="75%" height="6rem" style={{ borderTopLeftRadius: 0 }} />
              <Skeleton width="6rem" height="1rem" />
            </Group>
            <Skeleton width="100%" height="3.75rem" mt="xl" />
          </Group>
        </Group>
      </Paper>
    );
  }

  const onPostMessage = (message: string, appendDocuments: AppendDocument[]) => {
    const documents = appendDocuments.map((x) => x.documentId);
    const postMessage: Message = { message, documents };
    const tempMessageDocuments = appendDocuments.map((doc, index) => ({
      id: index,
      title: doc.fileName,
      fileName: doc.fileName,
      extension: '',
    }));
    const now = new Date().toISOString();
    const tempMessage: CaseMessage = {
      caseNoteId: 0,
      creationDate: now,
      author: null,
      noteText: message.replaceAll('\n', '<br />'),
      documents: tempMessageDocuments,
      posting: true,
    };
    const tempEvent: EventItem = {
      id: 'tempMessage',
      timestamp: now,
      type: 'message',
      message: tempMessage,
    };
    setItems((prevItems) => [...prevItems, tempEvent]);
    handlePostMessage(postMessage);
  };

  return (
    <div id="print-layer">
      <Paper className={classes.paper}>
        <ReverseScrollArea id="reverse-scroll-area">
          <Group p="xl" m="md" cypress="messages">
            <motion.div layout style={{ flex: 1 }}>
              {items &&
                items
                  .sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1))
                  ?.map((item, index) => (
                    <motion.div
                      key={item.id + index}
                      style={{ flex: 1 }}
                      layout
                      initial={{ y: 200 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <RenderMessages
                        item={item}
                        language={language}
                        username={username}
                        eventCaseCreatedText={stripText(messagesTexts.eventCaseCreated ?? '')}
                        eventCaseClosedText={stripText(messagesTexts.eventCaseClosed ?? '')}
                        errorText={stripText(errorTexts.tryAgain ?? '')}
                      />
                    </motion.div>
                  ))}
              {caseDetails.state === CaseState.closing && (
                <InfoMessage
                  text={stripText(messagesTexts.eventCaseClosedChatOpen ?? '')}
                  iconName="AlertCircle"
                />
              )}

              {caseDetails?.caseOwner?.outOfOffice?.enabled && (
                <InfoMessage
                  text={stripText(
                    messagesTexts.eventOutOfOffice
                      .replace('{caseWorker}', caseDetails?.caseOwner?.name)
                      .replace(
                        '{endTime}',
                        getFormattedDateWithWeekDayFromString(
                          caseDetails.caseOwner.outOfOffice.endTime,
                          language,
                        ),
                      ) ?? '',
                  )}
                  iconName="Clock"
                />
              )}

              {caseDetails.state !== CaseState.closed && !caseDetails?.caseOwner && (
                <InfoMessage
                  text={stripText(messagesTexts.eventNoCaseWorker ?? '')}
                  iconName="User"
                />
              )}
            </motion.div>
          </Group>
        </ReverseScrollArea>
        <OnlyShow smallerThan="sm">
          <div ref={scrollTOtargetRef} style={{ height: 280 }} className="hidden-in-print"></div>
        </OnlyShow>
        <Group
          grow
          px="xl"
          pb="l"
          pt="xs"
          m={0}
          direction="column"
          className={`${classes.messageWrapper} hidden-in-print`}
        >
          {caseDetails.state !== CaseState.closed ? (
            <CreateMessage
              placeholder={stripText(messagesTexts.placeholder ?? '')}
              fileUploadErrorTexts={errorTexts}
              handlePostMessage={onPostMessage}
              postingCaseState={postingCaseState}
            />
          ) : (
            <Paper className={classes.closed}>
              <HtmlText html={replaceHostName(messagesTexts?.closedCaseNoChat ?? '')} />
            </Paper>
          )}
          <Group grow pb={'xs'} m={0} className="hidden-in-print">
            <Link
              className={classes.printButton}
              onClick={() => window.print()}
              text={language === 'en' ? 'Print correspondance' : 'Udskriv korrespondance'}
              href={'#'}
              id="print-correspondance"
              cypress="print-correspondance"
            />
          </Group>
        </Group>
      </Paper>
    </div>
  );
};
